import Api from '../../scripts/api';
import Vue from 'vue';
/*
 * Vue storage for users
 *
 * Offers two methods to retrieve a user: by TYPO3 user ID and by FES user ID. The values stored are Promises, which
 * are resolved once the user was fetched from the API.
 *
 * Once retrieved, each user is stored in both maps and can be retrieved via each method.
 */

const FETCHING_FES_USER_ID = 'FETCHING_FES_USER_ID';
const FETCHING_TYPO3_USER_ID = 'FETCHING_TYPO3_USER_ID';
const FETCHING_USERS_SUCCESS = 'FETCHING_USERS_SUCCESS';

// As new Map() is not supported by Vue 2 we switch here to Object.create(null) which is a plain object and can be update with Vue.set
const state = {
    usersByFesUserId: Object.create(null),
    usersByTypo3Id: Object.create(null),
};

const getters = {
    // since getters cannot mutate the state, we can't use them here
};

const actions = {
    /**
     * Returns a user profile by the FES user ID
     *
     * @param {number} userId The FES user ID
     * @return {Promise<Array>} Returns user data from Getter/State
     */
    async getUserByFesUserId ({ commit }, userId) {
        if (!state.usersByFesUserId[userId]) {
            // store the resolve/reject methods from the promise for later use within Axios' promise
            let _resolve, _reject;

            const promise = new Promise((resolve, reject) => {
                _resolve = resolve;
                _reject = reject;
            });

            Api.version(1)
                .get('profiles/preview', { params: { userId: userId } })
                .then((userData) => {
                    _resolve(userData);
                    commit(FETCHING_USERS_SUCCESS, userData);
                });
            commit(FETCHING_FES_USER_ID, { userId, promise });
        }
        const userData = await state.usersByFesUserId[userId];
        return userData;
    },

    /**
     * Returns a user profile by the TYPO3 user ID
     *
     * @param {number} userId The TYPO3 user ID
     * @return {Array} Returns user data from Getter/State
     */
    async getUserByTypo3UserId ({ commit }, userId) {
        if (!state.usersByTypo3Id[userId]) {
            // store the resolve/reject methods from the promise for later use within Axios' promise
            let _resolve, _reject;

            const promise = new Promise((resolve, reject) => {
                _resolve = resolve;
                _reject = reject;
            });

            Api.version(1)
                .get('profiles/preview', { params: { frontendUserId: userId } })
                .then((userData) => {
                    _resolve(userData);
                    commit(FETCHING_USERS_SUCCESS, userData);
                });
            commit(FETCHING_TYPO3_USER_ID, { userId, promise });
        }
        const userData = await state.usersByTypo3Id[userId];
        return userData;
    },
};

const mutations = {
    FETCHING_FES_USER_ID (state, { userId, promise }) {
        Vue.set(state.usersByFesUserId, userId, promise);
    },
    FETCHING_TYPO3_USER_ID (state, { userId, promise }) {
        Vue.set(state.usersByTypo3Id, userId, promise);
    },
    FETCHING_USERS_SUCCESS (state, data) {
        if (data === null || data === undefined) {

        }
        // user was successfully retrieved from the API, we can now save the record in the other map as well
        const fesUserInMap = state.usersByFesUserId[data.id];
        const typo3UserInMap = state.usersByTypo3Id[data.frontendUserId];

        // we received this user by its TYPO3 user ID => also set it in the FES user ID map (which we did not know when initiating the fetch)
        if (typo3UserInMap) {
            Vue.set(state.usersByFesUserId, data.id, typo3UserInMap);
        }
        // we received this user by its FES user ID => resolve the promise, and set it in the TYPO3 user ID map
        if (fesUserInMap) {
            Vue.set(state.usersByTypo3Id, data.frontendUserId, fesUserInMap);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
